import React from 'react'
import { Container, Typography, Box, alpha, useTheme, Grid, Card, CardContent, Avatar, Stack, Chip } from '@mui/material'
import { Email, Phone, LocationOn, Business, Schedule } from '@mui/icons-material'
import Layout from '../components/Layout'
import ContactForm from '../components/ContactForm'
import { useTranslation } from 'react-i18next'
import { useColorMode } from '../theme'

const Kontakt: React.FC = () => {
    const theme = useTheme()
    const { t } = useTranslation()
    const { mode } = useColorMode()
    const isDark = mode === 'dark'

    const contactInfo = [
        {
            icon: <Email />,
            title: t('email'),
            value: 'hallo@webmix-it.de',
            description: t('writeUsMessage'),
            action: 'mailto:hallo@webmix-it.de'
        },
        {
            icon: <Phone />,
            title: t('phone'),
            value: t('onRequest'),
            description: t('feelFreeToCallUs'),
            action: null
        },
        {
            icon: <LocationOn />,
            title: t('location'),
            value: t('Germany'),
            description: t('remoteOnSitePossible'),
            action: null
        },
        {
            icon: <Business />,
            title: t('company'),
            value: 'Webmix-IT',
            description: t('itServicesAndSoftwareDevelopment'),
            action: null
        },
        {
            icon: <Schedule />,
            title: t('responseTime'),
            value: '< 24h',
            description: t('weRespondQuickly'),
            action: null
        }
    ]

    return (
        <Layout>

            {/* Hero Section */}
            <Box
                sx={{
                    py: { xs: 6, md: 8 },
                    background: isDark
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                mb: 3,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: { xs: '2.5rem', md: '3.5rem' }
                            }}
                        >
                            {t('contact')}
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                                maxWidth: 600,
                                mx: 'auto',
                                lineHeight: 1.6
                            }}
                        >
                            {t('contactPageSubtitle')}
                        </Typography>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Grid container spacing={6}>
                    {/* Contact Information */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    mb: 3,
                                    color: isDark ? '#ffffff' : theme.palette.primary.main
                                }}
                            >
                                {t('contactInformation')}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                    lineHeight: 1.6,
                                    mb: 4
                                }}
                            >
                                {t('contactSubtitle')}
                            </Typography>
                        </Box>

                        <Stack spacing={3}>
                            {contactInfo.map((info, index) => (
                                <Card
                                    key={index}
                                    sx={{
                                        p: 3,
                                        background: isDark
                                            ? `linear-gradient(135deg, ${alpha('#2d2d2d', 0.8)} 0%, ${alpha('#3d3d3d', 0.8)} 100%)`
                                            : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8f9ff', 0.9)} 100%)`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                                            borderColor: alpha(theme.palette.primary.main, 0.3)
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={3} alignItems="flex-start">
                                        <Avatar
                                            sx={{
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                color: theme.palette.primary.main,
                                                width: 48,
                                                height: 48
                                            }}
                                        >
                                            {info.icon}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 1,
                                                    color: isDark ? '#ffffff' : theme.palette.primary.main
                                                }}
                                            >
                                                {info.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    mb: 1,
                                                    color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'
                                                }}
                                            >
                                                {info.value}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'
                                                }}
                                            >
                                                {info.description}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Card>
                            ))}
                        </Stack>

                        {/* Quick Response Badge */}
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Chip
                                label={t('responseTimeBadge')}
                                sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    px: 2,
                                    py: 1,
                                    fontSize: '0.9rem'
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Contact Form */}
                    <Grid item xs={12} md={8}>
                        <Card
                            sx={{
                                p: 4,
                                background: isDark
                                    ? `linear-gradient(135deg, ${alpha('#2d2d2d', 0.8)} 0%, ${alpha('#3d3d3d', 0.8)} 100%)`
                                    : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8f9ff', 0.9)} 100%)`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                borderRadius: 4,
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 2,
                                        color: isDark ? '#ffffff' : theme.palette.primary.main
                                    }}
                                >
                                    {t('sendMessageTitle')}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                        mb: 4,
                                        lineHeight: 1.6
                                    }}
                                >
                                    {t('contactFormHelpText')}
                                </Typography>
                                <ContactForm />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Layout>
    )
}

export default Kontakt
